import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FullScreenConfirmOverlay from './components/FullScreenConfirmOverlay';
import PageHeader from './components/PageHeader';
import Pagination from './components/Pagination';
import SortableTableHeader from './components/SortableTableHeader';
import { showToastError, showToastSuccess } from './components/ToastNotifications';
import { getFacilities as fetchFacilities } from './hooks/useFacilities';
import { getAllMarkets } from './hooks/useMarkets';
import {
    getOrganizationsTotals,
    mergeQuickstartOrganization as requestMergeQuickstartOrganization,
} from './hooks/useOrganizations';
import { useLegacyAppSession } from './contexts/LegacyAppSessionContext';
import { getNextSortCriterion, type FieldSortCriterionValue } from './utils/tableSort';
interface IFacility {
    id: number | string;
    name?: string;
}
interface IFilters {
    nonquickstartSearchTerm: string;
    quickstartSearchTerm: string;
}
interface IMarket {
    id: number | string;
    name: string;
}
interface IOrganization {
    facilityIds?: Array<number | string>;
    facilityNames?: string;
    id: number | string;
    isQuickStart?: boolean;
    name: string;
    marketIds?: Array<number | string>;
    marketNames?: string;
}
interface IPagedResult {
    data: Array<IOrganization>;
    page: number;
    pagePending: number;
    pageSize: number;
    sort: FieldSortCriterionValue;
    total: number;
}

export default function OrganizationQuickstartMergePage({ organizationId }) {
    const navigate = useNavigate();
    const { currentUser } = useLegacyAppSession();
    const isSuperUser = currentUser?.isSuperUser === true;

    const [facilityOptions, setFacilityOptions] = useState<IFacility[]>([]);
    const [filters, setFilters] = useState<IFilters>({
        quickstartSearchTerm: '',
        nonquickstartSearchTerm: ''
    });
    const [quickstartLoading, setQuickstartLoading] = useState(false);
    const [nonquickstartLoading, setNonQuickstartLoading] = useState(false);
    const quickstartRequestIdRef = useRef(0);
    const nonquickstartRequestIdRef = useRef(0);
    const isLoading = quickstartLoading || nonquickstartLoading;
    const [isMerging, setIsMerging] = useState<boolean>(false);
    const [isMergeConfirmationOpen, setIsMergeConfirmationOpen] = useState<boolean>(false);
    const [marketOptions, setMarketOptions] = useState<IMarket[]>([]);
    const [nonquickstartOrganizations, setNonQuickstartOrganizations] = useState<IPagedResult>({
        page: 0,
        pagePending: 0,
        pageSize: 12,
        sort: { fieldName: 'name', isAscending: true },
        total: 0,
        data: []
    });
    const [quickstartOrganizations, setQuickstartOrganizations] = useState<IPagedResult>({
        page: 0,
        pagePending: 0,
        pageSize: 12,
        sort: { fieldName: 'name', isAscending: true },
        total: 0,
        data: []
    });
    const [selectedSourceOrganization, setSelectedSourceOrganization] = useState<IOrganization>(null);
    const [selectedTargetOrganization, setSelectedTargetOrganization] = useState<IOrganization>(null);

    function changeFilter(newSearchTerm, isQuickstart) {
        if (isQuickstart) {
            quickstartRequestIdRef.current += 1;
            setFilters((current) => ({ ...current, quickstartSearchTerm: newSearchTerm }));
            setQuickstartOrganizations((current) => ({ ...current, page: 0, pagePending: 0 }));
        } else {
            nonquickstartRequestIdRef.current += 1;
            setFilters((current) => ({ ...current, nonquickstartSearchTerm: newSearchTerm }));
            setNonQuickstartOrganizations((current) => ({ ...current, page: 0, pagePending: 0 }));
        }
    }

    function changePage(isQuickstart, pageOffset) {
        if (isQuickstart) {
            setQuickstartOrganizations((current) => ({ ...current, pagePending: current.page + pageOffset }));
        } else {
            setNonQuickstartOrganizations((current) => ({ ...current, pagePending: current.page + pageOffset }))
        }
    }

    function changeSort(isQuickstart: boolean, fieldName: string) {
        if (isQuickstart) {
            quickstartRequestIdRef.current += 1;
            setQuickstartOrganizations((current) => ({
                ...current,
                pagePending: 0,
                sort: getNextSortCriterion(current.sort, fieldName),
            }));
        } else {
            nonquickstartRequestIdRef.current += 1;
            setNonQuickstartOrganizations((current) => ({
                ...current,
                pagePending: 0,
                sort: getNextSortCriterion(current.sort, fieldName),
            }));
        }
    }

    async function getOrganizations(organizations: IPagedResult, isQuickstart: boolean) {
        if (!facilityOptions || !marketOptions) {
            return;
        }
        const requestIdRef = isQuickstart ? quickstartRequestIdRef : nonquickstartRequestIdRef;
        const requestId = ++requestIdRef.current;
        try {
            const { page, pagePending, pageSize, sort } = organizations;
            const searchTerm = isQuickstart ? filters.quickstartSearchTerm : filters.nonquickstartSearchTerm;

            const payload = {
                isQuickstart,
                name: searchTerm?.length === 0 ? null : searchTerm,
                page: pagePending ?? page,
                pageSize,
                sortField: sort.fieldName,
                sortAscending: sort.isAscending,
            };

            (isQuickstart ? setQuickstartLoading : setNonQuickstartLoading)(true);
            const response = await getOrganizationsTotals(payload);
            if (requestId !== requestIdRef.current) return;
            (isQuickstart ? setQuickstartLoading : setNonQuickstartLoading)(false);

            if (isQuickstart) {
                setQuickstartOrganizations((current) => ({
                    ...current,
                    data: response.data.map(o => mapOptionsToOrganization(o)),
                    page: pagePending,
                    pagePending,
                    pageSize: response.pageSize,
                    total: response.total
                }));
            } else {
                setNonQuickstartOrganizations((current) => ({
                    ...current,
                    data: response.data.map(o => mapOptionsToOrganization(o)),
                    page: pagePending,
                    pagePending,
                    pageSize: response.pageSize,
                    total: response.total
                }));
            }
        } catch {
            if (requestId !== requestIdRef.current) return;
            (isQuickstart ? setQuickstartLoading : setNonQuickstartLoading)(false);
            showToastError('Failed to load organizations. Please try again.');
        }
    }

    // Mapping function for facility/markets; simplification for backend
    function mapOptionsToOrganization(org: IOrganization) {
        org.marketNames = '';
        if (org.marketIds?.length) {
            for (const orgMarketId of org.marketIds) {
                for (const market of marketOptions) {
                    if (orgMarketId === market.id) {
                        if (org.marketNames.length > 0) {
                            org.marketNames += ', ';
                        }
                        org.marketNames += market.name;
                        break;
                    }
                }
            }
        }

        org.facilityNames = '';
        if (org.facilityIds?.length) {
            for (const orgFacilityId of org.facilityIds) {
                for (const facility of facilityOptions) {
                    if (orgFacilityId === facility.id) {
                        if (org.facilityNames.length > 0) {
                            org.facilityNames += ', ';
                        }
                        org.facilityNames += facility.name;
                        break;
                    }
                }
            }
        }

        return org;
    }

    async function mergeQuickstartOrganization() {
        if (!selectedSourceOrganization?.id) {
            showToastError('Source Organization must be selected in order to merge.');
            return;
        }
        if (!selectedTargetOrganization?.id) {
            showToastError('Target Organization must be selected in order to merge.');
            return;
        }
        try {
            setIsMerging(true);
            const response = await requestMergeQuickstartOrganization({
                sourceQuickstartOrganizationId: selectedSourceOrganization.id,
                targetOrganizationId: selectedTargetOrganization.id
            });
            setIsMerging(false);

            if (response?.succeeded) {
                showToastSuccess(`${selectedSourceOrganization.name} was successfully merged into ${selectedTargetOrganization.name}.`);
                setSelectedSourceOrganization(null);
                setSelectedTargetOrganization(null);
                // Retire the merged source immediately, even if refreshing fails.
                setQuickstartOrganizations(current => ({ ...current,
                    data: current.data.filter(org => org.id !== selectedSourceOrganization.id) }));
                await Promise.all([
                    getOrganizations({ ...quickstartOrganizations, pagePending: 0 }, true),
                    getOrganizations({ ...nonquickstartOrganizations, pagePending: 0 }, false),
                ]);
            } else {
                const failureReason = response?.failureReason
                    || response?.respBody?.message
                    || 'Could not merge organization';
                showToastError(failureReason);
            }
            setIsMergeConfirmationOpen(false);
        } catch {
            setIsMerging(false);
            showToastError('Organization merge failed. Please verify your selections and try again.');
        }
    }

    function promptUserToConfirmMerge(targetNonQuickstartOrg: IOrganization) {
        setSelectedTargetOrganization(targetNonQuickstartOrg);
        setIsMergeConfirmationOpen(true);
    }

    function selectSourceOrganization(organization: IOrganization) {
        if (!isSuperUser) {
            showToastError('Only super users can merge quickstart organizations.');
            return;
        }
        setSelectedSourceOrganization(organization);
        nonquickstartRequestIdRef.current += 1;
        setNonQuickstartLoading(false);
        setFilters((current) => ({ ...current, nonquickstartSearchTerm: '' }));
        setNonQuickstartOrganizations((current) => ({ ...current, page: 0, pagePending: 0 }));
    }

    useEffect(() => {
        async function getResults() {
            try {
                const response = await getAllMarkets(organizationId);
                setMarketOptions(response.sort((a, b) => a.name.localeCompare(b.name)));
            } catch {
                showToastError('Failed to load markets. Please try again.');
            }
        }
        if (organizationId)
            getResults();
    }, [organizationId])

    useEffect(() => {
        async function loadFacilities() {
            try {
                const response = await fetchFacilities(organizationId);
                setFacilityOptions(response);
            } catch {
                setFacilityOptions([]);
                showToastError('Failed to load facilities. Please try again.');
            }
        }
        if (organizationId)
            loadFacilities();
    }, [organizationId])

    useEffect(() => {
        getOrganizations(quickstartOrganizations, true);
    }, [facilityOptions, marketOptions, quickstartOrganizations.pagePending, quickstartOrganizations.sort.fieldName, quickstartOrganizations.sort.isAscending, filters.quickstartSearchTerm]);

    useEffect(() => {
        getOrganizations(nonquickstartOrganizations, false);
    }, [facilityOptions, marketOptions, nonquickstartOrganizations.pagePending, nonquickstartOrganizations.sort.fieldName, nonquickstartOrganizations.sort.isAscending, filters.nonquickstartSearchTerm]);

    function renderOrganizationsSearch(isQuickstart) {
        const searchTerm = isQuickstart ? filters.quickstartSearchTerm : filters.nonquickstartSearchTerm;
        return (
            <div className="card filter-card">
                <div className="filter-bar">
                    <div className="filter-left">
                        <input
                            className="input filter-search"
                            placeholder={`Search ${isQuickstart ? 'Quickstart' : 'Target'} organizations`}
                            value={searchTerm}
                            onChange={(e) => changeFilter(e.target.value, isQuickstart)}
                            data-testid={isQuickstart ? 'quickstart-organization-search' : 'target-organization-search'}
                        />
                    </div>
                </div>
            </div>
        );
    }

    function renderOrganizationsTable(isQuickstart) {
        const organizations = isQuickstart ? quickstartOrganizations : nonquickstartOrganizations;
        return (
            <div>
                <table className="table">
                    <thead>
                        <tr>
                            <SortableTableHeader label={`${isQuickstart ? 'Quickstart' : 'Target'} Organization`} fieldName="name" sort={organizations.sort} onSort={(fieldName) => changeSort(isQuickstart, fieldName)} />
                            <SortableTableHeader label="Market" fieldName="marketNames" sort={organizations.sort} onSort={(fieldName) => changeSort(isQuickstart, fieldName)} />
                            <SortableTableHeader label="Facility" fieldName="facilityNames" sort={organizations.sort} onSort={(fieldName) => changeSort(isQuickstart, fieldName)} />
                            <th aria-label="actions" />
                        </tr>
                    </thead>
                    <tbody>
                        {organizations.data.map((org) => (
                            <tr key={org.id}>
                                <td>{org.name}</td>
                                <td>{org.marketNames}</td>
                                <td>{org.facilityNames}</td>
                                <td style={{ textAlign: 'right' }}>
                                    {isQuickstart ?
                                        (<button className="button" disabled={isLoading} onClick={() => selectSourceOrganization(org)}>
                                            Select
                                        </button>) : (
                                        <button className="button" disabled={isLoading} onClick={() => promptUserToConfirmMerge(org)}>
                                            Merge
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <Pagination
                    page={organizations.page}
                    totalPages={Math.ceil(organizations.total / organizations.pageSize)}
                    onPrev={() => { changePage(isQuickstart, -1) }}
                    onNext={() => { changePage(isQuickstart, 1) }}
                    format="of"
                    prevDisabled={isLoading || isMerging}
                    nextDisabled={isLoading || isMerging}
                    hideWhenSingle={false}
                    prevLabel="◀"
                    nextLabel="▶"
                />
            </div>
        )
    }

    return (
        <div className="stack" data-testid="organization-quickstart-merge-page">
            <FullScreenConfirmOverlay
                show={isMergeConfirmationOpen}
                title="Merge Quickstart Organization"
                message={isMerging ?
                    `Merging ${selectedSourceOrganization?.name} into ${selectedTargetOrganization?.name}. This may take a few moments.` :
                    `Are you sure you want to merge ${selectedSourceOrganization?.name} into ${selectedTargetOrganization?.name}? This action cannot be undone.`}
                cancelLabel="Cancel"
                confirmLabel={isMerging ? 'Merging…' : 'Yes, merge permanently'}
                busy={isMerging}
                cancelHidden={isMerging}
                onCancel={() => {
                    if (isMerging) return;
                    setIsMergeConfirmationOpen(false);
                }}
                onConfirm={async () => {
                    if (isMerging) return;
                    mergeQuickstartOrganization();
                }}
                testId="products-archive-overlay"
            />

            <button className="button" type="button" onClick={() => navigate('/admin')} data-testid="organization-quickstart-merge-back" style={{ alignSelf: 'flex-start' }}>
                Back
            </button>
            <PageHeader
                title="Merge Quickstart Organizations"
                actions={
                    <div>
                        {selectedSourceOrganization ? (
                            <div>
                                <span>Quickstart Organization Selected: {selectedSourceOrganization.name}</span>
                                &nbsp;
                                {isMerging ? '' : (
                                    <button className="button" onClick={() => setSelectedSourceOrganization(null)}>Cancel Merge</button>
                                )}
                            </div>
                        ) : ''}
                    </div>
                }
            />

            {selectedSourceOrganization ? renderOrganizationsSearch(false) : renderOrganizationsSearch(true)}

            <div className="card">
                {selectedSourceOrganization ? renderOrganizationsTable(false) : renderOrganizationsTable(true)}
            </div>
        </div>
    );
}
